// CraftReview.jsx — WIRE-3: the Discovery-track human review queue sub-tab.
//
// Non-testable craft opinions (originality, AI-slop, product depth, design
// quality) land here as pending ReviewItems. A human approve/reject is the ONLY
// way one moves — THE LAW: nothing auto-applies. On approval the Worker returns
// the GATED learned_rule yaml (a proposal; a separate file step writes it).

import { useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { useCraftReviewQueue, setCraftReviewStatus } from '../../../../hooks/useCraftReview.js';

const CRITERION_TONES = {
  originality: 'bg-violet-900/40 text-violet-200',
  design_quality: 'bg-blue-900/40 text-blue-200',
  craft: 'bg-amber-900/40 text-amber-200',
  product_depth: 'bg-emerald-900/40 text-emerald-200',
};

export default function CraftReview() {
  const { data, loading, error, refetch } = useCraftReviewQueue('pending', { pollIntervalMs: 15_000 });
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [promotedYaml, setPromotedYaml] = useState(null);

  async function run(action, id) {
    setBusyId(id);
    setActionError(null);
    const status = action === 'approve' ? 'approved' : 'rejected';
    const res = await setCraftReviewStatus(id, status);
    setBusyId(null);
    if (!res.ok) {
      setActionError(res.error || `${action} failed`);
      return;
    }
    if (res.data?.promotion?.promoted && res.data.promotion.yaml) {
      setPromotedYaml(res.data.promotion.yaml);
    }
    refetch();
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-3 text-[11px] text-apolo-textTertiary" role="status">
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        Carregando fila de craft...
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="p-3 text-[11px] text-apolo-error">
        {error}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-[11px] text-apolo-textTertiary">
        <p>Nenhuma opinião de craft aguardando review.</p>
        <p className="text-[10px]">Opiniões não-testáveis (originality, AI-slop) aparecem aqui após um run verde.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {actionError && (
        <div role="alert" className="rounded border border-apolo-error/30 bg-apolo-error/10 px-2 py-1 text-[10px] text-apolo-error">
          {actionError}
        </div>
      )}
      {promotedYaml && (
        <div className="rounded border border-apolo-success/30 bg-apolo-success/10 px-2 py-1 text-[10px] text-apolo-success">
          Regra aprovada → learned_rule pronto (aplicação fica para o passo de arquivo, nunca automática).
          <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap text-apolo-textSecondary">{promotedYaml}</pre>
        </div>
      )}
      <ul aria-label="Fila de craft review" className="flex flex-col gap-2">
        {items.map((it) => {
          const tone = CRITERION_TONES[it.criterion] || 'bg-apolo-surface2 text-apolo-textSecondary';
          const isBusy = busyId === it.id;
          return (
            <li key={it.id} className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${tone}`}>
                  {it.criterion}
                </span>
                <span className="text-[10px] text-apolo-textTertiary">pendente</span>
              </div>
              <p className="text-[11px] leading-snug text-apolo-textPrimary">{it.suggestion}</p>
              <div className="mt-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => run('approve', it.id)}
                  disabled={isBusy}
                  aria-label={`Aprovar opinião ${it.criterion}`}
                  className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-success transition hover:border-apolo-success disabled:opacity-50"
                >
                  {isBusy ? <Loader2 size={10} className="animate-spin" aria-hidden="true" /> : <Check size={10} aria-hidden="true" />}
                  Aprovar
                </button>
                <button
                  type="button"
                  onClick={() => run('reject', it.id)}
                  disabled={isBusy}
                  aria-label={`Rejeitar opinião ${it.criterion}`}
                  className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-error transition hover:border-apolo-error disabled:opacity-50"
                >
                  <X size={10} aria-hidden="true" />
                  Rejeitar
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
